import * as React from 'react'
import { useIndustryResources } from './IndustryResourcesProvider'
import Sanitize from '~/components/job_search/Sanitize'
import { useTranslation } from '../../../hooks/useTranslation'
import { useIsMobile } from '../../../hooks/useMediaQuery'
import { Button } from '../../../lib/ui/button'
import { cn } from '../../../lib/utils'
import SearchResultsSavedStatus from '~/components/shared/SearchResultsSavedStatus'
import { LinkedInButton } from '~/components/shared/Buttons'

export default function ResourceDetails() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <ResourceDetailsTop />
      <ResourceDetailsBottom />
    </div>
  )
}

export function ResourceDetailsTop() {
  const {
    selectedResource: resource,
    debug,
    queryStatusMessage,
    isSavedResource,
  } = useIndustryResources()
  const isMobile = useIsMobile()
  if (!resource) return null

  return (
    <>
      <div className={`bg-primary text-white ${isMobile ? 'p-4' : 'p-0'}`}>
        {!isMobile && (
          <div className="relative flex flex-row justify-between">
            <div className="absolute right-0 top-0">
              {isSavedResource(resource) ? (
                <SavedToMyConnections />
              ) : (
                <AddResourceButton
                  resource={resource}
                  variant="icon"
                  includeText
                />
              )}
            </div>
          </div>
        )}

        <div
          className={`flex flex-col gap-4 ${!resource.city_state ? 'min-h-[68px]' : ''}`}
        >
          <div className="text-xl font-medium">{resource.name}</div>

          {resource.city_state && (
            <div className="flex items-center text-base font-normal">
              <div>{resource.city_state}</div>
            </div>
          )}

          {isMobile && (
            <div className="mt-4 flex">
              {isSavedResource(resource) ? (
                <SavedToMyConnections />
              ) : (
                <AddResourceButton
                  resource={resource}
                  variant="icon"
                  includeText
                />
              )}
            </div>
          )}
        </div>

        {debug && <pre className="break-all text-xs">{queryStatusMessage}</pre>}
      </div>
    </>
  )
}

const ResourceDetailsHeading = ({ className, children }) => {
  return (
    <div className={cn('text-base font-medium text-black', className)}>
      {children}
    </div>
  )
}

const ResourceDetailsDetails = ({ className, children }) => {
  return (
    <div className={cn('text-base font-normal', className)}>{children}</div>
  )
}

const ResourceDetailsLink = ({ className, children }) => {
  return (
    <ResourceDetailsDetails
      className={cn('text-blue-600 underline', className)}
    >
      {children}
    </ResourceDetailsDetails>
  )
}

const getLinkedInUrl = (resourceName) => {
  if (!resourceName) return ''
  return `https://www.linkedin.com/search/results/companies/?keywords=${resourceName}`
}

const formatUrl = (url) => {
  if (!url) return ''
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`
}

export function ResourceDetailsBottom() {
  const { selectedResource: resource } = useIndustryResources()
  const { t } = useTranslation('ResourceDetails')
  const currentYear = new Date().getFullYear()
  const isMobile = useIsMobile()

  const headingClasses = '!mr-16 w-40'

  if (!resource) return null

  return (
    <>
      {resource.address && (
        <div
          className={`mb-${isMobile ? '0' : '5'} flex ${
            isMobile ? 'flex-col' : ''
          }`}
        >
          <ResourceDetailsHeading className={headingClasses}>
            {t('address')}:
          </ResourceDetailsHeading>
          <ResourceDetailsDetails>{resource.address}</ResourceDetailsDetails>
        </div>
      )}

      {resource.phone && resource.phone !== '--' && (
        <div
          className={`mb-${isMobile ? '0' : '5'} flex ${
            isMobile ? 'flex-col' : ''
          }`}
        >
          <ResourceDetailsHeading className={headingClasses}>
            {t('phone')}:
          </ResourceDetailsHeading>
          <ResourceDetailsDetails>{resource.phone}</ResourceDetailsDetails>
        </div>
      )}

      {resource.email && (
        <div
          className={`mb-${isMobile ? '0' : '5'} flex ${
            isMobile ? 'flex-col' : ''
          }`}
        >
          <ResourceDetailsHeading className={headingClasses}>
            {t('email')}:
          </ResourceDetailsHeading>
          <ResourceDetailsLink>{resource.email}</ResourceDetailsLink>
        </div>
      )}

      {resource.website && (
        <div
          className={`mb-${isMobile ? '0' : '5'} flex ${
            isMobile ? 'flex-col' : ''
          }`}
        >
          <ResourceDetailsHeading className={headingClasses}>
            {t('website')}:
          </ResourceDetailsHeading>
          <ResourceDetailsLink>
            <a
              href={formatUrl(resource.website)}
              target="_blank"
              rel="noreferrer"
            >
              {resource.website}
            </a>
          </ResourceDetailsLink>
        </div>
      )}

      {resource.name && (
        <div
          className={`mb-${isMobile ? '0' : '5'} flex ${
            isMobile ? 'flex-col' : ''
          }`}
        >
          <ResourceDetailsHeading className={headingClasses}>
            {t('linkedin')}:
          </ResourceDetailsHeading>
          <ResourceDetailsLink>
            <a
              href={getLinkedInUrl(resource.name)}
              target="_blank"
              rel="noreferrer"
            >
              {resource.name}
            </a>
          </ResourceDetailsLink>
        </div>
      )}

      {resource.name && (
        <div className={`mb-${isMobile ? '0' : '5'}`}>
          <LinkedInButton companyName={resource.name}>
            <div>{t('see_connections')}</div>
          </LinkedInButton>
        </div>
      )}

      {resource.description && (
        <div className={`mb-${isMobile ? '0' : '5'}`}>
          <ResourceDetailsHeading>{t('description')}</ResourceDetailsHeading>
          <ResourceDetailsDetails>
            <Sanitize html={resource.description} addMarkup />
          </ResourceDetailsDetails>
        </div>
      )}

      {resource.contacts && resource.contacts.length > 0 && (
        <div className={`mb-${isMobile ? '0' : '5'}`}>
          <ResourceDetailsHeading>{t('contacts')}</ResourceDetailsHeading>
          <div className="divide-y divide-[#B9B9B9] text-base font-normal">
            {resource.contacts.map((contact, index) => (
              <ResourceContact
                key={`resource-contact-${index}`}
                contact={contact}
                resource={resource}
                isLast={index === resource.contacts.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <ResourceDetailsHeading>{t('source')}</ResourceDetailsHeading>
        <ResourceDetailsDetails>
          {t('source_info')} {currentYear}
        </ResourceDetailsDetails>
      </div>
    </>
  )
}

const ResourceContact = ({ contact, resource, isLast }) => {
  const { isSavedContact } = useIndustryResources()
  const isMobile = useIsMobile()
  return (
    <div
      className={`grid ${isMobile ? 'grid-cols-6' : 'grid-cols-5'} ${isLast ? 'pt-4' : 'py-4'}`}
    >
      <div className="col-span-4 flex">
        <div>
          {contact.full_name} - {contact.title}
        </div>{' '}
      </div>
      {isSavedContact(contact.external_id) ? (
        <SavedContact />
      ) : (
        <AddContactButton contact={contact} resource={resource} />
      )}
    </div>
  )
}

const SavedContact = () => {
  return (
    <div className="flex items-center gap-4 px-8 py-2">
      <CheckIcon />
      <div className="text-sm font-medium">Saved</div>
    </div>
  )
}

const AddContactButton = ({ contact }) => {
  const { t } = useTranslation('ResourceDetails')
  const { addToConnections, selectedResource } = useIndustryResources()
  const handleAddContact = () => {
    addToConnections(selectedResource, contact)
  }
  return (
    <Button onClick={handleAddContact} variant="link">
      {t('add_contact')}
    </Button>
  )
}

const AddResourceButton = () => {
  const { t } = useTranslation('ResourceDetails')
  const { selectedResource: resource, addToConnections } =
    useIndustryResources()
  const { contacts } = resource
  const contact = contacts && contacts[0]

  return (
    <Button
      className="flex rounded-full border border-white bg-transparent p-2 text-base font-medium text-white"
      type="button"
      onClick={() => addToConnections(resource, contact)}
    >
      {t('add_to_my_connections')}
    </Button>
  )
}

const CheckIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="size-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m4.5 12.75 6 6 9-13.5"
      />
    </svg>
  )
}

export function SavedToMyConnections() {
  const { t } = useTranslation('ResourceDetails')
  return (
    <SearchResultsSavedStatus title={t('saved_to_my_connections')}>
      <div className="text-sm font-medium text-white">
        {t('saved_to')}{' '}
        <span className="text-sm font-medium text-white">
          <a
            className="text-white !underline"
            href="/networking/my_connections"
          >
            {t('my_connections')}
          </a>
        </span>
      </div>
    </SearchResultsSavedStatus>
  )
}
